import React, { useEffect, useMemo, useState } from 'react';
import {
  ArrowTopRightOnSquareIcon,
  CalendarDaysIcon,
  CheckCircleIcon,
  ClipboardDocumentCheckIcon,
  MagnifyingGlassIcon,
  MapPinIcon,
  UserIcon,
  XMarkIcon,
} from '@heroicons/react/24/outline';
import { useNavigate } from 'react-router-dom';
import {
  searchFieldTrees,
  type FieldPlot,
  type TreeRecord,
} from '../../services/traceabilityApi';
import {
  createInspection,
  createInspectionFromSignal,
} from '../../services/treeInspectionApi';
import type { InspectionSignal, StaffOption } from './types';

interface Props {
  fields: FieldPlot[];
  trees: TreeRecord[];
  staff: StaffOption[];
  signal?: InspectionSignal | null;
  onClose: () => void;
  onCreated: () => void;
}

const inputClass = 'w-full rounded-md border border-slate-300 bg-white px-3 py-2.5 text-sm text-slate-900 outline-none transition focus:border-emerald-600 focus:ring-2 focus:ring-emerald-100';

const InspectionAssignmentDrawer: React.FC<Props> = ({ fields, trees, staff, signal, onClose, onCreated }) => {
  const navigate = useNavigate();
  const initialFieldId = signal?.field_plot_id ? String(signal.field_plot_id) : '';
  const initialTreeId = signal?.tree_id ? String(signal.tree_id) : '';
  const [fieldId, setFieldId] = useState(initialFieldId);
  const [fieldSearch, setFieldSearch] = useState('');
  const [treeId, setTreeId] = useState(initialTreeId);
  const [selectedTreeOption, setSelectedTreeOption] = useState<TreeRecord | null>(null);
  const [treeSearch, setTreeSearch] = useState('');
  const [fieldTreeOptions, setFieldTreeOptions] = useState<TreeRecord[]>([]);
  const [fieldTreeTotal, setFieldTreeTotal] = useState(0);
  const [treeOptionsFieldId, setTreeOptionsFieldId] = useState('');
  const [treesLoading, setTreesLoading] = useState(false);
  const [treeLoadError, setTreeLoadError] = useState('');
  const [assigneeId, setAssigneeId] = useState('');
  const [inspectionType, setInspectionType] = useState(signal ? 'follow_up' : 'routine');
  const [priority, setPriority] = useState(signal?.severity === 'critical' ? 'urgent' : signal?.severity === 'high' ? 'high' : 'normal');
  const [dueDate, setDueDate] = useState('');
  const [objective, setObjective] = useState(signal?.recommended_action || signal?.source_summary || '');
  const [workInstructions, setWorkInstructions] = useState('');
  const [safetyInstructions, setSafetyInstructions] = useState('');
  const [farmerAssistance, setFarmerAssistance] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState('');

  useEffect(() => {
    const closeOnEscape = (event: KeyboardEvent) => {
      if (event.key === 'Escape' && !submitting) onClose();
    };
    window.addEventListener('keydown', closeOnEscape);
    return () => window.removeEventListener('keydown', closeOnEscape);
  }, [onClose, submitting]);

  useEffect(() => {
    if (!fieldId) {
      setFieldTreeOptions([]);
      setFieldTreeTotal(0);
      setTreeOptionsFieldId('');
      setTreeLoadError('');
      return undefined;
    }

    const controller = new AbortController();
    const timer = window.setTimeout(async () => {
      setTreesLoading(true);
      setTreeLoadError('');
      try {
        const result = await searchFieldTrees(
          Number(fieldId),
          treeSearch,
          controller.signal,
        );
        setFieldTreeOptions(result.trees);
        setFieldTreeTotal(result.total);
        setTreeOptionsFieldId(fieldId);
      } catch (cause) {
        if (cause instanceof Error && cause.name === 'AbortError') return;
        setTreeLoadError('Live tree search is unavailable. Showing cached workspace trees.');
      } finally {
        if (!controller.signal.aborted) setTreesLoading(false);
      }
    }, 250);

    return () => {
      window.clearTimeout(timer);
      controller.abort();
    };
  }, [fieldId, treeSearch]);

  const filteredFields = useMemo(() => {
    const query = fieldSearch.trim().toLowerCase();
    if (!query) return fields;
    const matches = fields.filter((field) => [
      field.name,
      field.plot_code,
      field.crop_type,
      field.primary_farmer_name,
      field.farmer_name,
    ].some((value) => String(value || '').toLowerCase().includes(query)));
    const selected = fields.find((field) => String(field.id) === fieldId);
    return selected && !matches.some((field) => field.id === selected.id)
      ? [selected, ...matches]
      : matches;
  }, [fields, fieldId, fieldSearch]);
  const selectedField = useMemo(() => fields.find((field) => String(field.id) === fieldId), [fields, fieldId]);
  const cachedFieldTrees = useMemo(() => trees.filter((tree) => {
    if (!selectedField) return false;
    const belongsToField = (tree.plot_id != null && Number(tree.plot_id) === Number(selectedField.id))
      || tree.plot_code === selectedField.plot_code
      || tree.plot_name === selectedField.name;
    return belongsToField && tree.latitude != null && tree.longitude != null;
  }), [trees, selectedField]);
  const fieldTrees = treeOptionsFieldId === fieldId
    ? fieldTreeOptions
    : cachedFieldTrees;
  const filteredTrees = useMemo(() => {
    if (treeOptionsFieldId === fieldId) return fieldTrees;
    const query = treeSearch.trim().toLowerCase();
    if (!query) return fieldTrees;
    return fieldTrees.filter((tree) => [
      tree.tree_code,
      tree.health_status,
      tree.tree_status,
      tree.farmer_name,
    ].some((value) => String(value || '').toLowerCase().includes(query)));
  }, [fieldId, fieldTrees, treeOptionsFieldId, treeSearch]);
  const selectedTree = selectedTreeOption
    || fieldTrees.find((tree) => String(tree.id) === treeId)
    || cachedFieldTrees.find((tree) => String(tree.id) === treeId);
  const selectedAssignee = staff.find((person) => String(person.id) === assigneeId);
  const farmerId = selectedTree?.farmer_id || selectedField?.farmer_id || selectedField?.farmers?.find((farmer) => farmer.is_primary)?.id;

  const selectField = (nextFieldId: string) => {
    setFieldId(nextFieldId);
    setTreeId('');
    setSelectedTreeOption(null);
    setTreeSearch('');
    setFieldTreeOptions([]);
    setFieldTreeTotal(0);
    setTreeOptionsFieldId('');
    setTreeLoadError('');
    setError('');
    const field = fields.find((item) => String(item.id) === nextFieldId);
    const suggested = field?.assigned_agronomist_id;
    if (suggested && staff.some((person) => Number(person.id) === Number(suggested))) {
      setAssigneeId(String(suggested));
    }
  };

  const submit = async () => {
    if (!fieldId || !assigneeId) {
      setError('Select a mapped field and the agronomist responsible for this inspection.');
      return;
    }
    if (!objective.trim()) {
      setError('Add a clear inspection objective so the agronomist knows what to verify.');
      return;
    }
    if (treeId && (!selectedTree || Number(selectedTree.plot_id) !== Number(fieldId))) {
      setError('The selected tree no longer belongs to this field. Choose another tree.');
      return;
    }
    setSubmitting(true);
    setError('');
    const payload = {
      assigned_to: Number(assigneeId),
      field_plot_id: Number(fieldId),
      tree_id: treeId ? Number(treeId) : null,
      farmer_id: farmerId || null,
      inspection_type: inspectionType,
      priority,
      due_date: dueDate || null,
      objective: objective.trim(),
      work_instructions: workInstructions.trim() || null,
      safety_instructions: safetyInstructions.trim() || null,
      farmer_assistance_needed: farmerAssistance,
      source_type: signal?.source_type || 'manual',
      source_id: signal?.source_id || null,
      source_summary: signal?.source_summary || signal?.title || null,
    };
    try {
      if (signal) await createInspectionFromSignal(signal.id, payload);
      else await createInspection(payload);
      onCreated();
    } catch (cause) {
      setError(cause instanceof Error ? cause.message : 'Unable to assign this inspection.');
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div className="fixed inset-0 z-50 flex justify-end bg-slate-950/45" role="dialog" aria-modal="true">
      <button className="absolute inset-0 cursor-default" onClick={onClose} aria-label="Close assignment drawer" />
      <aside className="relative flex h-full w-full max-w-3xl flex-col bg-white shadow-2xl">
        <header className="flex items-start justify-between border-b border-slate-200 px-6 py-5">
          <div>
            <p className="text-xs font-bold uppercase tracking-widest text-emerald-700">Traceability operation</p>
            <h2 className="mt-1 text-xl font-bold text-slate-950">Assign tree inspection</h2>
            <p className="mt-1 text-sm text-slate-500">Select the field first, then target a mapped tree and assign the responsible agronomist.</p>
          </div>
          <button onClick={onClose} className="rounded-md p-2 text-slate-500 hover:bg-slate-100" aria-label="Close">
            <XMarkIcon className="h-5 w-5" />
          </button>
        </header>

        <div className="flex-1 space-y-6 overflow-y-auto px-4 py-5 sm:px-6 sm:py-6">
          {signal && (
            <section className="border-l-4 border-amber-500 bg-amber-50 px-4 py-3">
              <p className="text-xs font-bold uppercase text-amber-700">Inspection signal</p>
              <p className="mt-1 font-semibold text-amber-950">{signal.title}</p>
              <p className="mt-1 text-sm text-amber-800">{signal.source_summary || 'Operational evidence requires manager review.'}</p>
            </section>
          )}

          <section className="rounded-md border border-slate-200 bg-white p-4 sm:p-5">
            <div className="mb-3 flex items-center justify-between">
              <div>
                <h3 className="font-bold text-slate-900">1. Inspection scope</h3>
                <p className="text-sm text-slate-500">The tree list comes directly from the selected Traceability field.</p>
              </div>
              <button type="button" onClick={() => navigate('/dashboard/traceability')} className="flex items-center gap-1 text-xs font-bold text-emerald-700 hover:text-emerald-900">
                Open Traceability <ArrowTopRightOnSquareIcon className="h-4 w-4" />
              </button>
            </div>
            <div className="grid gap-5 lg:grid-cols-2">
              <div>
                <label className="text-sm font-semibold text-slate-700" htmlFor="inspection-field-search">Field / plot *</label>
                <div className="relative mt-1.5">
                  <MagnifyingGlassIcon className="pointer-events-none absolute left-3 top-3 h-4 w-4 text-slate-400" />
                  <input id="inspection-field-search" className={`${inputClass} pl-9`} value={fieldSearch} onChange={(event) => setFieldSearch(event.target.value)} placeholder="Search field, code, crop, farmer..." />
                </div>
                <select aria-label="Select mapped field" className={`${inputClass} mt-2`} value={fieldId} onChange={(event) => selectField(event.target.value)}>
                  <option value="">Select mapped field</option>
                  {filteredFields.map((field) => <option key={field.id} value={field.id}>{field.name || field.plot_code} · {field.crop_type || 'Crop not set'}</option>)}
                </select>
                <p className="mt-1.5 text-xs text-slate-500">{filteredFields.length} of {fields.length} mapped fields</p>
              </div>
              <div>
                <label className="text-sm font-semibold text-slate-700" htmlFor="inspection-tree-search">Target tree</label>
                <div className="relative mt-1.5">
                  <MagnifyingGlassIcon className="pointer-events-none absolute left-3 top-3 h-4 w-4 text-slate-400" />
                  <input id="inspection-tree-search" className={`${inputClass} pl-9`} value={treeSearch} disabled={!fieldId} onChange={(event) => setTreeSearch(event.target.value)} placeholder="Search code, health, status, farmer..." />
                </div>
                <div className="mt-2 max-h-52 overflow-y-auto rounded-md border border-slate-200 bg-slate-50 p-1.5">
                  <button type="button" disabled={!fieldId} onClick={() => { setTreeId(''); setSelectedTreeOption(null); }} className={`flex w-full items-center justify-between rounded px-3 py-2 text-left text-sm ${fieldId && !treeId ? 'bg-emerald-700 font-bold text-white' : 'text-slate-700 hover:bg-white disabled:text-slate-400'}`}>
                    <span>Whole field inspection</span><span className="text-xs opacity-75">All trees</span>
                  </button>
                  {fieldId && filteredTrees.slice(0, 50).map((tree) => {
                    const selected = String(tree.id) === treeId;
                    return <button type="button" key={tree.id} onClick={() => { setTreeId(String(tree.id)); setSelectedTreeOption(tree); }} className={`mt-1 flex w-full items-center justify-between gap-3 rounded px-3 py-2 text-left ${selected ? 'bg-emerald-700 text-white' : 'bg-white text-slate-700 hover:bg-emerald-50'}`}>
                      <span className="min-w-0"><span className="block truncate text-sm font-bold">{tree.tree_code}</span><span className={`block truncate text-xs ${selected ? 'text-emerald-100' : 'text-slate-500'}`}>{tree.farmer_name || 'Farmer not linked'}</span></span>
                      <span className={`shrink-0 rounded-full px-2 py-0.5 text-xs font-bold ${selected ? 'bg-white/15 text-white' : 'bg-slate-100 text-slate-600'}`}>{String(tree.health_status || tree.tree_status || 'unknown').replace(/_/g, ' ')}</span>
                    </button>;
                  })}
                  {fieldId && treesLoading && <p className="px-3 py-5 text-center text-sm text-slate-500">Loading mapped trees…</p>}
                  {fieldId && !treesLoading && filteredTrees.length === 0 && <p className="px-3 py-5 text-center text-sm text-slate-500">No trees match this search. You can still assign a whole-field inspection.</p>}
                </div>
                {fieldId && <span className="mt-1.5 block text-xs text-slate-500">Showing {Math.min(filteredTrees.length, 50)} of {treeOptionsFieldId === fieldId ? fieldTreeTotal : fieldTrees.length} mapped trees</span>}
                {treeLoadError && <span className="mt-1.5 block text-xs font-semibold text-amber-700">{treeLoadError}</span>}
              </div>
            </div>
            {selectedField && (
              <div className="mt-3 flex flex-wrap gap-x-5 gap-y-2 border-y border-slate-200 py-3 text-sm text-slate-600">
                <span className="flex items-center gap-1"><MapPinIcon className="h-4 w-4 text-emerald-700" />{selectedField.name || selectedField.plot_code}</span>
                <span>{selectedField.crop_type || 'Crop not set'}</span>
                <span>{treeOptionsFieldId === fieldId ? fieldTreeTotal : fieldTrees.length} mapped trees available</span>
                <span>{selectedField.primary_farmer_name || selectedField.farmer_name || 'Farmer team not assigned'}</span>
              </div>
            )}
          </section>

          <section className="rounded-md border border-slate-200 bg-white p-4 sm:p-5">
            <h3 className="font-bold text-slate-900">2. Responsibility and schedule</h3>
            <div className="mt-3 grid gap-4 sm:grid-cols-2">
              <label className="text-sm font-semibold text-slate-700">Agronomist *
                <select className={`${inputClass} mt-1.5`} value={assigneeId} onChange={(event) => setAssigneeId(event.target.value)}>
                  <option value="">Select agronomist</option>
                  {staff.map((person) => <option key={person.id} value={person.id}>{person.full_name} · {person.role.replace(/_/g, ' ')}</option>)}
                </select>
                {selectedAssignee && <span className="mt-1 flex items-center gap-1 text-xs font-normal text-emerald-700"><UserIcon className="h-3.5 w-3.5" />Active workspace agronomist</span>}
              </label>
              <label className="text-sm font-semibold text-slate-700">Due date
                <div className="relative mt-1.5"><CalendarDaysIcon className="pointer-events-none absolute left-3 top-3 h-4 w-4 text-slate-400" /><input className={`${inputClass} pl-9`} type="date" min={new Date().toISOString().slice(0, 10)} value={dueDate} onChange={(event) => setDueDate(event.target.value)} /></div>
              </label>
              <label className="text-sm font-semibold text-slate-700">Inspection type
                <select className={`${inputClass} mt-1.5`} value={inspectionType} onChange={(event) => setInspectionType(event.target.value)}>
                  <option value="routine">Routine</option><option value="scheduled">Scheduled</option><option value="follow_up">Follow-up</option><option value="emergency">Emergency</option>
                </select>
              </label>
              <label className="text-sm font-semibold text-slate-700">Priority
                <select className={`${inputClass} mt-1.5`} value={priority} onChange={(event) => setPriority(event.target.value)}>
                  <option value="low">Low</option><option value="normal">Normal</option><option value="high">High</option><option value="urgent">Urgent</option>
                </select>
              </label>
            </div>
          </section>

          <section className="rounded-md border border-slate-200 bg-white p-4 sm:p-5">
            <h3 className="font-bold text-slate-900">3. Instructions</h3>
            <div className="mt-3 space-y-4">
              <label className="block text-sm font-semibold text-slate-700">Inspection objective *
                <textarea className={`${inputClass} mt-1.5`} rows={3} value={objective} onChange={(event) => setObjective(event.target.value)} placeholder="Explain the concern and what must be verified." />
                <span className="mt-1 block text-right text-xs font-normal text-slate-400">{objective.trim().length} characters</span>
              </label>
              <label className="block text-sm font-semibold text-slate-700">Work instructions
                <textarea className={`${inputClass} mt-1.5`} rows={3} value={workInstructions} onChange={(event) => setWorkInstructions(event.target.value)} placeholder="Checks, sample points, measurements, and evidence required." />
              </label>
              <label className="block text-sm font-semibold text-slate-700">Safety instructions
                <textarea className={`${inputClass} mt-1.5`} rows={2} value={safetyInstructions} onChange={(event) => setSafetyInstructions(event.target.value)} placeholder="PPE, chemical handling, access, or field hazards." />
              </label>
              <label className="flex items-center gap-3 text-sm font-semibold text-slate-700">
                <input type="checkbox" checked={farmerAssistance} onChange={(event) => setFarmerAssistance(event.target.checked)} className="h-4 w-4 rounded border-slate-300 text-emerald-700" />
                Farmer assistance may be required during this inspection
              </label>
            </div>
          </section>

          {error && <div className="border border-red-200 bg-red-50 px-4 py-3 text-sm font-semibold text-red-700">{error}</div>}
        </div>

        <footer className="flex flex-col gap-3 border-t border-slate-200 bg-slate-50 px-4 py-4 sm:flex-row sm:items-center sm:justify-between sm:px-6">
          <div className="flex items-center gap-2 text-xs text-slate-500">
            <CheckCircleIcon className="h-4 w-4 text-emerald-700" /> Assignment will appear in the agronomist mobile portal.
          </div>
          <div className="grid grid-cols-2 gap-3 sm:flex">
            <button onClick={onClose} disabled={submitting} className="rounded-md border border-slate-300 bg-white px-4 py-2.5 text-sm font-bold text-slate-700 hover:bg-slate-100 disabled:opacity-50">Cancel</button>
            <button onClick={submit} disabled={submitting || !fieldId || !assigneeId || !objective.trim()} className="flex items-center justify-center gap-2 rounded-md bg-emerald-700 px-5 py-2.5 text-sm font-bold text-white hover:bg-emerald-800 disabled:cursor-not-allowed disabled:opacity-50">
              <ClipboardDocumentCheckIcon className="h-4 w-4" />{submitting ? 'Assigning…' : 'Assign inspection'}
            </button>
          </div>
        </footer>
      </aside>
    </div>
  );
};

export default InspectionAssignmentDrawer;
